import { CommonInputProps } from 'components/forms/inputs/rhf-inputs/InputTypes';
import { ModalActions } from 'components/modals/useModal';
import cloneDeep from 'lodash/cloneDeep';
import { Fragment } from 'react';
import { UseFormSetValue, useWatch } from 'react-hook-form';
import { EmployeeForm, EmploymentForm } from 'types/employees';
import { NamedEntityMinDto } from 'types/other';
import { getEmploymentGapLabel, sortEmploymentHistoryByValidFrom } from '../util';
import AddEmploymentButton, { Position } from './AddEmploymentButton';
import { EmploymentBreak } from './EmploymentBreak';
import { EmploymentUnit } from './EmploymentUnit';

interface Props extends CommonInputProps<EmployeeForm> {
    setValue: UseFormSetValue<EmployeeForm>;
    readonly: boolean;
    jobRoleList: NamedEntityMinDto[];
    jobPositionList: NamedEntityMinDto[];
    modalActions: ModalActions;
}

export function EmploymentHistory({ control, readonly, setValue, jobRoleList, jobPositionList, modalActions }: Props) {
    const employmentHistory = useWatch({ control, name: `employmentHistory` });
    const sortedEmploymentHistory = cloneDeep(employmentHistory ?? []);
    sortEmploymentHistoryByValidFrom(sortedEmploymentHistory);

    const updateEmploymentHandler = (employment: EmploymentForm) => {
        const employmentHistoryState = cloneDeep(employmentHistory ?? []);
        const index = employmentHistoryState.findIndex((item) => item.recordId === employment.recordId);
        if (index === -1 || !Array.isArray(employmentHistoryState) || !employmentHistoryState[index]) {
            return;
        }
        employmentHistoryState[index] = { ...employment };
        setValue('employmentHistory', employmentHistoryState);
    };

    const deleteEmploymentHandler = (recordId: number) => {
        const employmentHistoryState = cloneDeep(employmentHistory ?? []);
        setValue(
            'employmentHistory',
            employmentHistoryState.filter((item) => item.recordId !== recordId)
        );
    };

    return sortedEmploymentHistory.length !== 0 ? (
        <div>
            {sortedEmploymentHistory.map((employment, index) => {
                const newerEmployment = sortedEmploymentHistory[index - 1];
                const gapLabel = newerEmployment
                    ? getEmploymentGapLabel(employment.validTo, newerEmployment.validFrom)
                    : null;
                return (
                    <Fragment key={employment.recordId}>
                        {gapLabel && <EmploymentBreak label={gapLabel} />}
                        <EmploymentUnit
                            employment={employment}
                            readonly={readonly}
                            jobRoleList={jobRoleList}
                            jobPositionList={jobPositionList}
                            onSave={updateEmploymentHandler}
                            onDelete={deleteEmploymentHandler}
                            modalActions={modalActions}
                        />
                    </Fragment>
                );
            })}
        </div>
    ) : (
        <div className="flex justify-center">
            {!readonly && (
                <AddEmploymentButton
                    position={Position.HISTORY_SIDE}
                    control={control}
                    setValue={setValue}
                    jobRoleList={jobRoleList}
                    jobPositionList={jobPositionList}
                    modalActions={modalActions}
                />
            )}
        </div>
    );
}
